import React, { useState } from 'react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { GradeBadge, readable } from './CausalGrade';
import { toneOf } from './CausalEffectBlock';

const fmt = (x, scale = 1) => {
  if (x === null || x === undefined) return '—';
  const v = x * scale;
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 1) return v.toFixed(2);
  return v.toPrecision(3);
};

const sign = (v) => (v > 0 ? '+' : '');
// A segment effect next to zero reads as "≈ 0" rather than "-0.00370".
const signedEffect = (v, scale, ref) => (
  Math.abs(v) < 0.01 * Math.max(Math.abs(ref || 0), 1) ? '≈ 0' : `${sign(v)}${fmt(v, scale)}`
);

const Stat = ({ label, value, badge, badgeTone, hint, muted }) => (
  <div className="q-stat">
    <span className="q-stat__label">{label}</span>
    <span className="q-stat__value" style={muted ? { color: 'var(--muted-foreground)' } : undefined}>
      {value}
      {badge && <Badge variant={badgeTone || 'outline'} className="ui-badge--mono">{badge}</Badge>}
    </span>
    {hint && <span className="q-stat__hint">{hint}</span>}
  </div>
);

/** Horizontal effect bar with its 95% interval, centred on zero. */
const EffectBar = ({ effect, ciLow, ciHigh, maxAbs }) => {
  const pct = (v) => 50 + (v / maxAbs) * 48; // map [-maxAbs, maxAbs] → [2%, 98%]
  const left = Math.min(pct(0), pct(effect));
  const width = Math.abs(pct(effect) - pct(0));
  const significant = !(ciLow <= 0 && ciHigh >= 0);
  const tone = significant ? 'var(--chart-1)' : 'var(--muted-foreground)';
  return (
    <div className="q-segfx__track">
      <div className="q-segfx__zero" />
      <div
        className="q-segfx__ci"
        style={{ left: `${Math.min(pct(ciLow), pct(ciHigh))}%`, width: `${Math.abs(pct(ciHigh) - pct(ciLow))}%` }}
      />
      <div
        className="q-segfx__bar"
        style={{ left: `${left}%`, width: `${Math.max(width, 0.7)}%`, background: tone }}
      />
      <div className="q-segfx__point" style={{ left: `${pct(effect)}%`, background: tone }} />
    </div>
  );
};

// A segment is a path down the tree ("industry ≠ a and industry = b"). The
// equality clauses define it; a path of only exclusions is "everything else".
export const ruleText = (rule) => {
  const clauses = String(rule || '').split(' and ').map((c) => c.trim()).filter(Boolean);
  const defining = clauses.filter((c) => c.includes(' = '));
  if (!clauses.length) return '';
  if (!defining.length) {
    const excluded = clauses.map((c) => c.split(' ≠ ')[1]).filter(Boolean);
    return `All other (not ${excluded.join(', ')})`.replace(/_/g, ' ');
  }
  return defining.map((c) => c.split(' = ')[1] || c).join(', ').replace(/_/g, ' ');
};

/**
 * Heterogeneous treatment effects: the average effect and the effect per segment
 * in one card.
 * Props: blockData = cate_segments payload from tableyze.causal.reporting.
 */
const CateSegmentsBlock = ({ blockData }) => {
  const data = blockData || {};
  const overall = data.overall_effect || {};
  const scale = overall.display_scale || 1;
  const segments = data.segments || [];
  const breakdowns = (data.breakdowns || []).filter((b) => (b.levels || []).length > 1);
  const [active, setActive] = useState(0);
  const breakdown = breakdowns[Math.min(active, breakdowns.length - 1)];
  // One row per value of the chosen column when the backend sent per-level
  // effects; otherwise the summary tree's segments.
  const rows = breakdown
    ? breakdown.levels.map((r) => ({ ...r, label: readable(r.level) }))
    : segments.map((r) => ({ ...r, label: ruleText(r.rule) }));
  const confidence = data.confidence || {};
  const maxAbs = Math.max(
    ...rows.flatMap((s) => [Math.abs(s.ci_low), Math.abs(s.ci_high), Math.abs(s.effect)]),
    Math.abs(overall.point || 0),
    1e-9,
  );
  const significant = !(overall.ci_low <= 0 && overall.ci_high >= 0);
  const ranked = [...rows].sort((a, b) => b.effect - a.effect);
  const strongest = ranked.length > 1 ? ranked[0] : null;
  const weakest = ranked.length > 1 ? ranked[ranked.length - 1] : null;
  const treatmentCol = (data.treatment || {}).column;
  const outcomeCol = (data.outcome || {}).column;
  const modifiers = (data.top_modifiers || []).map(readable);
  const description = [
    treatmentCol && outcomeCol ? `${readable(treatmentCol)} → ${readable(outcomeCol)}` : null,
    modifiers.length ? `differs most by ${modifiers.join(', ')}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Block
      icon="split"
      title="Effect by segment"
      description={description || null}
      badge={confidence.grade ? <GradeBadge grade={confidence.grade} /> : null}
      footer="Dots mark each estimate, whiskers its 95% interval; grey means not distinguishable from zero."
    >
      <div className="q-stats">
        <Stat
          label="Average effect"
          value={`${sign(overall.point)}${fmt(overall.point, scale)}`}
          badge={overall.relative_pct != null ? `${sign(overall.relative_pct)}${overall.relative_pct}%` : null}
          badgeTone={overall.relative_pct != null ? toneOf(overall.relative_pct) : null}
          muted={!significant}
          hint={`95% CI ${fmt(overall.ci_low, scale)} to ${fmt(overall.ci_high, scale)}`}
        />
        {strongest && (
          <Stat
            label="Strongest segment"
            value={signedEffect(strongest.effect, scale, overall.point)}
            hint={`${strongest.label} · n=${strongest.n.toLocaleString()}`}
          />
        )}
        {weakest && (
          <Stat
            label="Weakest segment"
            value={signedEffect(weakest.effect, scale, overall.point)}
            muted={weakest.ci_low <= 0 && weakest.ci_high >= 0}
            hint={`${weakest.label} · n=${weakest.n.toLocaleString()}`}
          />
        )}
      </div>
      {overall.units && <p className="q-causal__context">{readable(overall.units)}</p>}

      {breakdowns.length > 1 && (
        <div className="q-segfx__tabs" role="tablist" aria-label="Break the effect down by">
          {breakdowns.map((b, i) => (
            <button
              key={b.column}
              type="button"
              role="tab"
              aria-selected={b === breakdown}
              className={`q-segfx__tab${b === breakdown ? ' is-active' : ''}`}
              onClick={() => setActive(i)}
            >
              By {readable(b.column)}
            </button>
          ))}
        </div>
      )}
      <div className="q-segfx">
        {rows.map((s, i) => (
          <div key={i} className="q-segfx__row">
            <div className="q-segfx__head">
              <span className="q-segfx__rule">{s.label}</span>
              <span className="q-segfx__value q-num">
                {signedEffect(s.effect, scale, overall.point)}
                {s.relative_pct != null && (
                  <Badge
                    variant={s.ci_low <= 0 && s.ci_high >= 0 ? 'outline' : toneOf(s.relative_pct)}
                    className="ui-badge--mono q-segfx__pct"
                  >
                    {`${sign(s.relative_pct)}${s.relative_pct}%`}
                  </Badge>
                )}
                <span className="q-muted"> · {fmt(s.ci_low, scale)} to {fmt(s.ci_high, scale)} · n={s.n.toLocaleString()}</span>
              </span>
            </div>
            <EffectBar effect={s.effect} ciLow={s.ci_low} ciHigh={s.ci_high} maxAbs={maxAbs} />
          </div>
        ))}
      </div>
    </Block>
  );
};

export default CateSegmentsBlock;
